/**
 * El cierre «Postúlate» de la descripción publicada de una vacante.
 *
 * El diseño lo pinta como un aviso azul al final de la descripción, pero el
 * texto no es un campo aparte: viene dentro del mismo texto enriquecido que
 * captura RH. Por eso se separa aquí en vez de pedir una columna nueva — el
 * dato ya existe y partirlo al mostrarlo no obliga a recapturar nada.
 *
 * Si la descripción no trae ese cierre, no se inventa: el cuerpo sale completo
 * y el aviso simplemente no aparece.
 */

/** Etiquetas de bloque donde puede empezar el cierre. */
const APERTURAS = ['<p', '<div', '<h1', '<h2', '<h3', '<h4', '<h5', '<h6']

export interface DescripcionPartida {
  /** Todo lo que va antes del cierre. */
  cuerpo: string
  /** El cierre, o null si la descripción no lo trae. */
  llamado: string | null
}

export function separaElLlamado(html: string | null | undefined): DescripcionPartida {
  if (!html) return { cuerpo: '', llamado: null }

  // Con y sin acento: lo escriben de las dos formas.
  const posicion = html.search(/post[úu]late\s*:/i)
  if (posicion === -1) return { cuerpo: html, llamado: null }

  // Se retrocede hasta la etiqueta de bloque que abre ese párrafo, para no
  // partir el HTML a media etiqueta y dejar el marcado roto.
  const antes = html.slice(0, posicion)
  const inicio = Math.max(...APERTURAS.map((t) => antes.lastIndexOf(t)))

  if (inicio < 0) return { cuerpo: '', llamado: html }

  return { cuerpo: html.slice(0, inicio), llamado: html.slice(inicio) }
}

/**
 * El cierre en un solo renglón.
 *
 * Viene como dos párrafos —el «Postúlate:» y la invitación— y dentro del aviso
 * cada uno abre su propia línea. Se quitan las etiquetas de bloque y se deja un
 * espacio en su lugar: el texto corre seguido y las negritas se conservan.
 *
 * No se usa CSS para esto porque poner los párrafos en `inline` los pega sin
 * separación: el HTML no trae espacio entre el cierre de uno y la apertura del
 * siguiente.
 */
export function enUnaLinea(html: string | null | undefined): string {
  if (!html) return ''

  return html
    .replace(/<(p|div|h[1-6])\b[^>]*>/gi, '')
    .replace(/<\/(p|div|h[1-6])>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
